import { useState, useMemo, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { SuperAdminLayout } from "@/components/super-admin/SuperAdminLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Pagination,
  PaginationContent,
  PaginationEllipsis,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from "@/components/ui/pagination";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Skeleton } from "@/components/ui/skeleton";
import { useToast } from "@/hooks/use-toast";
import { useIsMobile } from "@/hooks/use-mobile";
import { Search, MoreVertical, Eye, LogOut, Users as UsersIcon, UserCog, Shield, Trash2, Building2, Key, Mail, Bell, BellOff } from "lucide-react";
import { cn } from "@/lib/utils";
import type { UserInfo, UserFilters, QuickFilterCounts, GymOption, AppOption } from "@/types/superAdmin";
import { ForceSignOutDialog } from "@/components/super-admin/ForceSignOutDialog";
import { UserDetailsDialog } from "@/components/super-admin/UserDetailsDialog";
import { DeleteUserDialog } from "@/components/super-admin/DeleteUserDialog";
import { ForcePasswordDialog } from "@/components/super-admin/ForcePasswordDialog";
import { SendResetLinkDialog } from "@/components/super-admin/SendResetLinkDialog";
import { QuickFilters } from "@/components/super-admin/QuickFilters";
import { AdvancedFilters } from "@/components/super-admin/AdvancedFilters";

export default function SuperAdminUsers() {
  const { t } = useTranslation("superadmin2");
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const isMobile = useIsMobile();
  const [searchQuery, setSearchQuery] = useState("");
  const [roleFilter, setRoleFilter] = useState<string>("all");
  const [statusFilter, setStatusFilter] = useState<string>("all");
  const [selectedUser, setSelectedUser] = useState<UserInfo | null>(null);
  const [userDetailsOpen, setUserDetailsOpen] = useState(false);
  const [forceSignOutOpen, setForceSignOutOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [forcePasswordOpen, setForcePasswordOpen] = useState(false);
  const [sendResetLinkOpen, setSendResetLinkOpen] = useState(false);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(20);
  
  // Advanced filters state
  const [quickFilter, setQuickFilter] = useState<string | null>(null);
  const [advancedFilters, setAdvancedFilters] = useState<UserFilters>({});

  // Fetch gyms and apps list (only once)
  const { data: gymsAndApps } = useQuery({
    queryKey: ['super-admin-gyms-apps'],
    queryFn: async () => {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) throw new Error('No session');

      const { data, error } = await supabase.functions.invoke('get-all-users', {
        headers: { Authorization: `Bearer ${session.access_token}` },
        body: { 
          page: 0, 
          pageSize: 1,
          fetchGymsAndApps: true,
        }
      });

      if (error) throw error;
      return { gyms: data.gyms as GymOption[], apps: data.apps as AppOption[] };
    },
    staleTime: 5 * 60 * 1000, // Cache for 5 minutes
  });

  // Fetch counts for quick filters
  const { data: filterCounts } = useQuery({
    queryKey: ['super-admin-filter-counts'],
    queryFn: async () => {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) throw new Error('No session');

      const { data, error } = await supabase.functions.invoke('get-all-users', {
        headers: { Authorization: `Bearer ${session.access_token}` },
        body: { 
          page: 0, 
          pageSize: 1,
          fetchCounts: true,
        }
      });

      if (error) throw error;
      return data.counts as QuickFilterCounts;
    },
    staleTime: 60 * 1000, // Cache for 1 minute
  });

  // Build request body based on active filters
  const buildRequestBody = () => {
    const body: any = { 
      page, 
      pageSize,
      role: roleFilter !== 'all' ? roleFilter : undefined,
      status: statusFilter !== 'all' ? statusFilter : undefined,
      searchQuery: searchQuery || undefined,
    };

    // Apply quick filter
    if (quickFilter) {
      switch (quickFilter) {
        case 'multiGym':
          body.multiGymOnly = true;
          break;
        case 'noSubscription':
          body.subscriptionStatus = 'none';
          break;
        case 'neverConnected':
          body.neverConnected = true;
          break;
        case 'inactive30d':
          body.inactiveDays = 30;
          break;
        case 'newThisWeek':
          const oneWeekAgo = new Date();
          oneWeekAgo.setDate(oneWeekAgo.getDate() - 7);
          body.createdAfter = oneWeekAgo.toISOString();
          break;
        case 'expiredSubscription':
          body.subscriptionStatus = 'expired';
          break;
      }
    }

    // Apply advanced filters
    if (advancedFilters.gymId) body.gymId = advancedFilters.gymId;
    if (advancedFilters.appName) body.appName = advancedFilters.appName;
    if (advancedFilters.subscriptionStatus && !body.subscriptionStatus) {
      body.subscriptionStatus = advancedFilters.subscriptionStatus;
    }
    if (advancedFilters.searchFields && advancedFilters.searchFields.length > 0) {
      body.searchFields = advancedFilters.searchFields;
    }

    return body;
  };

  // Fetch users with pagination and filters
  const { data: usersData, isLoading } = useQuery({
    queryKey: ['super-admin-all-users', page, pageSize, roleFilter, statusFilter, searchQuery, quickFilter, advancedFilters],
    queryFn: async () => {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) throw new Error('No session');

      const { data, error } = await supabase.functions.invoke('get-all-users', {
        headers: { Authorization: `Bearer ${session.access_token}` },
        body: buildRequestBody()
      });

      if (error) throw error;
      return data;
    },
  });

  const allUsers = usersData?.users || [];
  const totalUsers = usersData?.total || 0;
  const totalPages = Math.ceil(totalUsers / pageSize);

  // Force sign out mutation
  const forceSignOutMutation = useMutation({
    mutationFn: async ({ userId, scope }: { userId: string; scope: 'global' | 'local' | 'others' }) => {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) throw new Error('No session');

      const { data, error } = await supabase.functions.invoke('force-signout-user', {
        headers: { Authorization: `Bearer ${session.access_token}` },
        body: { userId, scope },
      });

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      toast({
        title: t("users.signedOutTitle"),
        description: t("users.signedOutDesc"),
      });
      queryClient.invalidateQueries({ queryKey: ['super-admin-all-users'] });
      setForceSignOutOpen(false);
      setSelectedUser(null);
    },
    onError: (error: any) => {
      toast({
        title: t("users.errorTitle"),
        description: error.message || t("users.signOutErrorDesc"),
        variant: "destructive",
      });
    },
  });

  // Force password mutation
  const forcePasswordMutation = useMutation({
    mutationFn: async ({ userId, newPassword, signOutAll, sendEmail }: { userId: string; newPassword: string; signOutAll: boolean; sendEmail: boolean }) => {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) throw new Error('No session');

      const { data, error } = await supabase.functions.invoke('force-user-password', {
        headers: { Authorization: `Bearer ${session.access_token}` },
        body: { userId, newPassword, signOutAll, sendEmail },
      });

      if (error) throw error;
      return data;
    },
    onSuccess: (data) => {
      toast({
        title: t("users.passwordChangedTitle"),
        description: data.emailSent 
          ? t("users.passwordChangedSentDesc") 
          : t("users.passwordChangedDesc"),
      });
      queryClient.invalidateQueries({ queryKey: ['super-admin-all-users'] });
      setForcePasswordOpen(false);
      setSelectedUser(null);
    },
    onError: (error: any) => {
      toast({
        title: t("users.errorTitle"),
        description: error.message || t("users.passwordErrorDesc"),
        variant: "destructive",
      });
    },
  });

  // Send reset link mutation
  const sendResetLinkMutation = useMutation({
    mutationFn: async ({ userId }: { userId: string }) => {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) throw new Error('No session');

      const { data, error } = await supabase.functions.invoke('send-password-reset-admin', {
        headers: { Authorization: `Bearer ${session.access_token}` },
        body: { userId },
      });

      if (error) throw error;
      return data;
    },
    onSuccess: (data) => {
      toast({
        title: t("users.emailSentTitle"),
        description: t("users.emailSentDesc", { email: data.maskedEmail }),
      });
      setSendResetLinkOpen(false);
      setSelectedUser(null);
    },
    onError: (error: any) => {
      toast({
        title: t("users.errorTitle"),
        description: error.message || t("users.emailErrorDesc"),
        variant: "destructive",
      });
    },
  });

  // Get statistics from current page (approximation)
  const stats = useMemo(() => ({
    total: totalUsers,
    admins: allUsers.filter(u => u.roles.some(r => r.role === 'admin')).length,
    coaches: allUsers.filter(u => u.roles.some(r => r.role === 'coach')).length,
    clients: allUsers.filter(u => u.roles.some(r => r.role === 'client')).length,
  }), [allUsers, totalUsers]);

  // Reset to first page when filters change
  const handleRoleFilterChange = (value: string) => {
    setRoleFilter(value);
    setPage(0);
  };

  const handleStatusFilterChange = (value: string) => {
    setStatusFilter(value);
    setPage(0);
  };

  const handlePageSizeChange = (value: string) => {
    setPageSize(Number(value));
    setPage(0);
  };

  const handleQuickFilterChange = (filter: string | null) => {
    setQuickFilter(filter);
    setPage(0);
  };

  const handleAdvancedFiltersChange = (filters: UserFilters) => {
    setAdvancedFilters(filters);
    setPage(0);
  };

  const handleSearchChange = (value: string) => {
    setSearchQuery(value);
    setPage(0);
  };

  // Generate page numbers for pagination
  const pageNumbers = useMemo(() => {
    const pages: number[] = [];
    const maxVisible = isMobile ? 3 : 5;
    
    if (totalPages <= maxVisible) {
      for (let i = 0; i < totalPages; i++) {
        pages.push(i);
      }
    } else {
      if (page < 2) {
        pages.push(0, 1, 2);
      } else if (page >= totalPages - 2) {
        pages.push(totalPages - 3, totalPages - 2, totalPages - 1);
      } else {
        pages.push(page - 1, page, page + 1);
      }
    }
    
    return pages;
  }, [page, totalPages, isMobile]);

  const getActivityBadge = (lastSignIn: string | null) => {
    const badgeClass = isMobile ? "text-[8px] px-1 py-0" : "";
    
    if (!lastSignIn) return <Badge variant="secondary" className={badgeClass}>{t("users.never")}</Badge>;
    
    const lastDate = new Date(lastSignIn);
    const now = new Date();
    const hoursDiff = (now.getTime() - lastDate.getTime()) / (1000 * 60 * 60);
    
    if (hoursDiff < 1) {
      return <Badge className={cn("bg-green-500", badgeClass)}>{t("users.online")}</Badge>;
    } else if (hoursDiff < 24) {
      return <Badge className={cn("bg-blue-500", badgeClass)}>{isMobile ? t("users.activeTodayShort") : t("users.activeToday")}</Badge>;
    } else if (hoursDiff < 168) {
      return <Badge variant="secondary" className={badgeClass}>{isMobile ? t("users.activeThisWeekShort") : t("users.activeThisWeek")}</Badge>;
    } else {
      return <Badge variant="outline" className={badgeClass}>{t("users.inactive")}</Badge>;
    }
  };

  const getSubscriptionBadge = (status?: string, endDate?: string | null) => {
    const badgeClass = isMobile ? "text-[8px] px-1 py-0" : "";
    
    if (!status || status === 'none') {
      return <Badge variant="outline" className={cn(badgeClass, "text-muted-foreground")}>{t("users.none")}</Badge>;
    }
    
    switch (status) {
      case 'active':
        return <Badge className={cn("bg-green-500", badgeClass)}>{t("users.active")}</Badge>;
      case 'expired':
        return <Badge className={cn("bg-red-500", badgeClass)}>{t("users.expired")}</Badge>;
      case 'paused':
        return <Badge className={cn("bg-yellow-500", badgeClass)}>{t("users.paused")}</Badge>;
      default:
        return <Badge variant="outline" className={badgeClass}>{status}</Badge>;
    }
  };

  const roleColors: Record<string, string> = {
    admin: "bg-purple-500",
    coach: "bg-blue-500",
    client: "bg-green-500",
    super_admin: "bg-red-500",
  };

  const roleLabels: Record<string, string> = {
    admin: t("users.admin"),
    coach: t("users.coach"),
    client: t("users.client"),
    super_admin: t("users.superAdmin"),
  };

  return (
    <SuperAdminLayout>
      <div className="space-y-6">
        {/* Header */}
        <div>
          <h1 className="text-3xl font-bold text-foreground">{t("users.title")}</h1>
          <p className="text-muted-foreground">{t("users.subtitle")}</p>
        </div>

        {/* Stats Cards */}
        <div className={cn(
          "grid gap-4",
          isMobile ? "grid-cols-2" : "grid-cols-2 md:grid-cols-4"
        )}>
          <Card>
            <CardHeader className={isMobile ? "p-3 pb-1" : "pb-2"}>
              <CardTitle className={cn("font-medium text-muted-foreground", isMobile ? "text-xs" : "text-sm")}>
                {isMobile ? t("users.totalUsersShort") : t("users.totalUsersFull")}
              </CardTitle>
            </CardHeader>
            <CardContent className={isMobile ? "p-3 pt-0" : ""}>
              <div className="flex items-center gap-2">
                <UsersIcon className={cn("text-muted-foreground", isMobile ? "h-3 w-3" : "h-4 w-4")} />
                <span className={cn("font-bold text-foreground", isMobile ? "text-lg" : "text-2xl")}>{stats.total}</span>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className={isMobile ? "p-3 pb-1" : "pb-2"}>
              <CardTitle className={cn("font-medium text-muted-foreground", isMobile ? "text-xs" : "text-sm")}>{t("users.admins")}</CardTitle>
            </CardHeader>
            <CardContent className={isMobile ? "p-3 pt-0" : ""}>
              <div className="flex items-center gap-2">
                <Shield className={cn("text-muted-foreground", isMobile ? "h-3 w-3" : "h-4 w-4")} />
                <span className={cn("font-bold text-foreground", isMobile ? "text-lg" : "text-2xl")}>{stats.admins}</span>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className={isMobile ? "p-3 pb-1" : "pb-2"}>
              <CardTitle className={cn("font-medium text-muted-foreground", isMobile ? "text-xs" : "text-sm")}>{t("users.coaches")}</CardTitle>
            </CardHeader>
            <CardContent className={isMobile ? "p-3 pt-0" : ""}>
              <div className="flex items-center gap-2">
                <UserCog className={cn("text-muted-foreground", isMobile ? "h-3 w-3" : "h-4 w-4")} />
                <span className={cn("font-bold text-foreground", isMobile ? "text-lg" : "text-2xl")}>{stats.coaches}</span>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className={isMobile ? "p-3 pb-1" : "pb-2"}>
              <CardTitle className={cn("font-medium text-muted-foreground", isMobile ? "text-xs" : "text-sm")}>{t("users.clients")}</CardTitle>
            </CardHeader>
            <CardContent className={isMobile ? "p-3 pt-0" : ""}>
              <div className="flex items-center gap-2">
                <UsersIcon className={cn("text-muted-foreground", isMobile ? "h-3 w-3" : "h-4 w-4")} />
                <span className={cn("font-bold text-foreground", isMobile ? "text-lg" : "text-2xl")}>{stats.clients}</span>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Quick Filters */}
        <Card>
          <CardHeader className={isMobile ? "p-4 pb-2" : ""}>
            <CardTitle className={cn("", isMobile ? "text-base" : "")}>{t("users.quickSearches")}</CardTitle>
            <CardDescription className={cn("", isMobile ? "text-xs" : "")}>
              {t("users.quickSearchesDesc")}
            </CardDescription>
          </CardHeader>
          <CardContent className={isMobile ? "p-4 pt-2" : ""}>
            <QuickFilters
              counts={filterCounts || {
                multiGym: 0,
                noSubscription: 0,
                neverConnected: 0,
                inactive30d: 0,
                newThisWeek: 0,
                expiredSubscription: 0,
              }}
              activeFilter={quickFilter}
              onFilterChange={handleQuickFilterChange}
              isLoading={isLoading}
            />
          </CardContent>
        </Card>

        {/* Search and Filters */}
        <Card>
          <CardHeader className={isMobile ? "p-4 pb-2" : ""}>
            <CardTitle className={cn("", isMobile ? "text-base" : "")}>{t("users.filters")}</CardTitle>
            <CardDescription className={cn("", isMobile ? "text-xs" : "")}>
              {t("users.filtersDesc")}
            </CardDescription>
          </CardHeader>
          <CardContent className={cn("space-y-4", isMobile ? "p-4 pt-2" : "")}>
            <div className={cn("flex gap-4", isMobile ? "flex-col" : "flex-col md:flex-row")}>
              <div className={isMobile ? "w-full" : "flex-1"}>
                <div className="relative">
                  <Search className={cn("absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground", isMobile ? "h-3 w-3" : "h-4 w-4")} />
                  <Input
                    placeholder={isMobile ? t("users.searchShort") : t("users.searchFull")}
                    value={searchQuery}
                    onChange={(e) => handleSearchChange(e.target.value)}
                    className={cn("pl-10", isMobile ? "text-sm" : "")}
                  />
                </div>
              </div>
              
              <div className={cn("flex gap-2", isMobile ? "w-full" : "")}>
                <Select value={roleFilter} onValueChange={handleRoleFilterChange}>
                  <SelectTrigger className={cn("", isMobile ? "flex-1 text-sm" : "w-full md:w-48")}>
                    <SelectValue placeholder={t("users.role")} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">{t("users.allRoles")}</SelectItem>
                    <SelectItem value="super_admin">{t("users.superAdmin")}</SelectItem>
                    <SelectItem value="admin">{t("users.admin")}</SelectItem>
                    <SelectItem value="coach">{t("users.coach")}</SelectItem>
                    <SelectItem value="client">{t("users.client")}</SelectItem>
                  </SelectContent>
                </Select>

                <Select value={statusFilter} onValueChange={handleStatusFilterChange}>
                  <SelectTrigger className={cn("", isMobile ? "flex-1 text-sm" : "w-full md:w-48")}>
                    <SelectValue placeholder={t("users.status")} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">{t("users.allStatuses")}</SelectItem>
                    <SelectItem value="active">{t("users.active")}</SelectItem>
                    <SelectItem value="inactive">{t("users.inactive")}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Advanced Filters */}
            <AdvancedFilters
              filters={advancedFilters}
              onFiltersChange={handleAdvancedFiltersChange}
              gyms={gymsAndApps?.gyms || []}
              apps={gymsAndApps?.apps || []}
              isLoading={isLoading}
            />
          </CardContent>
        </Card>

        {/* Users Table */}
        <Card>
          <CardHeader className={isMobile ? "p-4 pb-2" : ""}>
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-2">
              <CardTitle className={cn("", isMobile ? "text-base" : "")}>
                {t("users.usersCount", { count: totalUsers })}
              </CardTitle>
              <div className={cn("flex items-center gap-2", isMobile ? "text-xs" : "text-sm")}>
                <span className="text-muted-foreground">{t("users.rowsPerPage")}</span>
                <Select value={pageSize.toString()} onValueChange={handlePageSizeChange}>
                  <SelectTrigger className={cn("", isMobile ? "w-16 h-8 text-xs" : "w-20")}>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="10">10</SelectItem>
                    <SelectItem value="20">20</SelectItem>
                    <SelectItem value="50">50</SelectItem>
                    <SelectItem value="100">100</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </CardHeader>
          <CardContent className={isMobile ? "p-0" : ""}>
            {isLoading ? (
              <div className="space-y-2 p-4">
                {Array.from({ length: pageSize }).map((_, i) => (
                  <Skeleton key={i} className="h-12 w-full" />
                ))}
              </div>
            ) : allUsers.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">{t("users.noUsersFound")}</div>
            ) : (
              <div className="overflow-hidden">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>{t("users.user")}</TableHead>
                      <TableHead>{t("users.roles")}</TableHead>
                      {!isMobile && <TableHead>{t("users.gyms")}</TableHead>}
                      {!isMobile && <TableHead>{t("users.app")}</TableHead>}
                      {!isMobile && <TableHead>{t("users.notif")}</TableHead>}
                      {!isMobile && <TableHead>{t("users.subscription")}</TableHead>}
                      {!isMobile && <TableHead>{t("users.lastConnection")}</TableHead>}
                      <TableHead className="text-right">{t("users.actions")}</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {allUsers.map((user) => {
                    const fullName = `${user.first_name} ${user.last_name}`.trim() || user.email;
                    const initials = user.first_name && user.last_name
                      ? `${user.first_name[0]}${user.last_name[0]}`.toUpperCase()
                      : user.email[0].toUpperCase();

                    return (
                      <TableRow key={user.user_id}>
                        <TableCell>
                          <div className={cn("flex items-center", isMobile ? "gap-2" : "gap-3")}>
                            {!isMobile && (
                              <Avatar className={isMobile ? "h-6 w-6" : ""}>
                                <AvatarFallback className={isMobile ? "text-xs" : ""}>{initials}</AvatarFallback>
                              </Avatar>
                            )}
                            <div>
                              <div className={cn("font-medium", isMobile ? "text-xs" : "")}>{fullName}</div>
                              <div className={cn("text-muted-foreground", isMobile ? "text-[10px]" : "text-sm")}>{user.email}</div>
                            </div>
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="flex flex-wrap gap-1">
                            {user.roles.map((role, idx) => (
                              <Badge key={idx} className={cn(roleColors[role.role] || "bg-gray-500", isMobile ? "text-[8px] px-1 py-0" : "")}>
                                {isMobile ? roleLabels[role.role]?.substring(0, 1) : (roleLabels[role.role] || role.role)}
                              </Badge>
                            ))}
                          </div>
                        </TableCell>
                        {!isMobile && (
                          <TableCell>
                            <TooltipProvider>
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <div className="flex items-center gap-1">
                                    {(user.gym_count || 0) > 1 ? (
                                      <Badge className="bg-purple-500 text-white">
                                        <Building2 className="h-3 w-3 mr-1" />
                                        {t("users.multi", { count: user.gym_count })}
                                      </Badge>
                                    ) : (
                                      <span className="text-sm text-muted-foreground">
                                        {user.roles
                                          .filter(r => r.gym_name)
                                          .map(r => r.gym_name)
                                          .filter((v, i, a) => a.indexOf(v) === i)
                                          .slice(0, 1)
                                          .join("") || "—"}
                                      </span>
                                    )}
                                  </div>
                                </TooltipTrigger>
                                {(user.gym_count || 0) > 1 && (
                                  <TooltipContent>
                                    <div className="space-y-1">
                                      {user.roles
                                        .filter(r => r.gym_name)
                                        .map(r => r.gym_name)
                                        .filter((v, i, a) => a.indexOf(v) === i)
                                        .map((gymName, idx) => (
                                          <div key={idx}>{gymName}</div>
                                        ))}
                                    </div>
                                  </TooltipContent>
                                )}
                              </Tooltip>
                            </TooltipProvider>
                          </TableCell>
                        )}
                        {!isMobile && (
                          <TableCell>
                            <div className="flex flex-wrap gap-1">
                              {user.app_names && user.app_names.length > 0 ? (
                                user.app_names.map((appName, idx) => (
                                  <Badge key={idx} variant="outline" className="text-xs">
                                    {appName}
                                  </Badge>
                                ))
                              ) : (
                                <span className="text-muted-foreground">—</span>
                              )}
                            </div>
                          </TableCell>
                        )}
                        {!isMobile && (
                          <TableCell>
                            <TooltipProvider>
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <div className="flex items-center">
                                    {(user.device_count || 0) > 0 ? (
                                      <Badge className="bg-green-500 text-white">
                                        <Bell className="h-3 w-3 mr-1" />
                                        {user.device_count}
                                      </Badge>
                                    ) : (
                                      <Badge variant="outline" className="text-muted-foreground">
                                        <BellOff className="h-3 w-3 mr-1" />
                                        0
                                      </Badge>
                                    )}
                                  </div>
                                </TooltipTrigger>
                                <TooltipContent>
                                  {(user.device_count || 0) > 0 ? (
                                    <div className="space-y-1">
                                      <div className="font-medium">{t("users.devicesRegistered", { count: user.device_count })}</div>
                                      {user.notification_apps && user.notification_apps.length > 0 && (
                                        <div className="text-xs text-muted-foreground">
                                          {t("users.apps", { apps: user.notification_apps.join(', ') })}
                                        </div>
                                      )}
                                    </div>
                                  ) : (
                                    <div>{t("users.noDeviceRegistered")}</div>
                                  )}
                                </TooltipContent>
                              </Tooltip>
                            </TooltipProvider>
                          </TableCell>
                        )}
                        {!isMobile && (
                          <TableCell>
                            {getSubscriptionBadge(user.subscription_status, user.subscription_end_date)}
                          </TableCell>
                        )}
                        {!isMobile && (
                          <TableCell>
                            {getActivityBadge(user.last_sign_in_at)}
                          </TableCell>
                        )}
                        <TableCell className="text-right">
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button variant="ghost" size="icon" className={cn("hover:bg-muted", isMobile ? "h-6 w-6" : "")}>
                                <MoreVertical className={isMobile ? "h-3 w-3" : "h-4 w-4"} />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              <DropdownMenuItem
                                onClick={() => {
                                  setSelectedUser(user);
                                  setUserDetailsOpen(true);
                                }}
                              >
                                <Eye className="h-4 w-4 mr-2" />
                                {t("users.viewProfile")}
                              </DropdownMenuItem>
                              <DropdownMenuSeparator />
                              <DropdownMenuItem
                                onClick={() => {
                                  setSelectedUser(user);
                                  setForcePasswordOpen(true);
                                }}
                                className="text-orange-600"
                              >
                                <Key className="h-4 w-4 mr-2" />
                                {t("users.forcePassword")}
                              </DropdownMenuItem>
                              <DropdownMenuItem
                                onClick={() => {
                                  setSelectedUser(user);
                                  setSendResetLinkOpen(true);
                                }}
                              >
                                <Mail className="h-4 w-4 mr-2" />
                                {t("users.sendResetLink")}
                              </DropdownMenuItem>
                              <DropdownMenuSeparator />
                              <DropdownMenuItem
                                onClick={() => {
                                  setSelectedUser(user);
                                  setForceSignOutOpen(true);
                                }}
                                className="text-orange-600"
                              >
                                <LogOut className="h-4 w-4 mr-2" />
                                {t("users.forceSignOut")}
                              </DropdownMenuItem>
                              <DropdownMenuSeparator />
                              <DropdownMenuItem
                                onClick={() => {
                                  setSelectedUser(user);
                                  setDeleteDialogOpen(true);
                                }}
                                className="text-red-600"
                              >
                                <Trash2 className="h-4 w-4 mr-2" />
                                {t("users.deleteUser")}
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                  </TableBody>
                </Table>
              </div>
            )}
            
            {/* Pagination */}
            {!isLoading && allUsers.length > 0 && (
              <div className={cn("flex flex-col gap-4 border-t", isMobile ? "p-4" : "p-4 pt-6")}>
                <div className={cn("text-muted-foreground text-center", isMobile ? "text-xs" : "text-sm")}>
                  {t("users.showingRange", { from: page * pageSize + 1, to: Math.min((page + 1) * pageSize, totalUsers), total: totalUsers })}
                </div>
                
                <Pagination>
                  <PaginationContent className={isMobile ? "gap-1" : ""}>
                    <PaginationItem>
                      <PaginationPrevious 
                        onClick={() => setPage(p => Math.max(0, p - 1))}
                        className={cn(
                          "cursor-pointer",
                          page === 0 && "pointer-events-none opacity-50",
                          isMobile && "h-8 px-2 text-xs"
                        )}
                      />
                    </PaginationItem>
                    
                    {!isMobile && page > 1 && (
                      <>
                        <PaginationItem>
                          <PaginationLink
                            onClick={() => setPage(0)}
                            className="cursor-pointer"
                          >
                            1
                          </PaginationLink>
                        </PaginationItem>
                        {page > 2 && <PaginationEllipsis />}
                      </>
                    )}
                    
                    {pageNumbers.map(pageNum => (
                      <PaginationItem key={pageNum}>
                        <PaginationLink
                          onClick={() => setPage(pageNum)}
                          isActive={page === pageNum}
                          className={cn(
                            "cursor-pointer",
                            isMobile && "h-8 w-8 text-xs"
                          )}
                        >
                          {pageNum + 1}
                        </PaginationLink>
                      </PaginationItem>
                    ))}
                    
                    {!isMobile && page < totalPages - 2 && (
                      <>
                        {page < totalPages - 3 && <PaginationEllipsis />}
                        <PaginationItem>
                          <PaginationLink
                            onClick={() => setPage(totalPages - 1)}
                            className="cursor-pointer"
                          >
                            {totalPages}
                          </PaginationLink>
                        </PaginationItem>
                      </>
                    )}
                    
                    <PaginationItem>
                      <PaginationNext 
                        onClick={() => setPage(p => Math.min(totalPages - 1, p + 1))}
                        className={cn(
                          "cursor-pointer",
                          page >= totalPages - 1 && "pointer-events-none opacity-50",
                          isMobile && "h-8 px-2 text-xs"
                        )}
                      />
                    </PaginationItem>
                  </PaginationContent>
                </Pagination>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Dialogs */}
      <UserDetailsDialog
        open={userDetailsOpen}
        onOpenChange={setUserDetailsOpen}
        user={selectedUser}
      />

      <ForceSignOutDialog
        open={forceSignOutOpen}
        onOpenChange={setForceSignOutOpen}
        user={selectedUser}
        onConfirm={(userId, scope) => forceSignOutMutation.mutate({ userId, scope })}
        isLoading={forceSignOutMutation.isPending}
      />

      <DeleteUserDialog
        open={deleteDialogOpen}
        onOpenChange={setDeleteDialogOpen}
        user={selectedUser}
        onDeleteSuccess={() => {
          toast({
            title: t("users.deletedTitle"),
            description: t("users.deletedDesc"),
          });
          queryClient.invalidateQueries({ queryKey: ['super-admin-all-users'] });
          queryClient.invalidateQueries({ queryKey: ['super-admin-filter-counts'] });
          setSelectedUser(null);
        }}
      />

      <ForcePasswordDialog
        open={forcePasswordOpen}
        onOpenChange={setForcePasswordOpen}
        user={selectedUser}
        onConfirm={(userId, password, signOutAll, sendEmail) => 
          forcePasswordMutation.mutate({ userId, newPassword: password, signOutAll, sendEmail })
        }
        isLoading={forcePasswordMutation.isPending}
      />

      <SendResetLinkDialog
        open={sendResetLinkOpen}
        onOpenChange={setSendResetLinkOpen}
        user={selectedUser}
        onConfirm={(userId) => sendResetLinkMutation.mutate({ userId })}
        isLoading={sendResetLinkMutation.isPending}
      />
    </SuperAdminLayout>
  );
}
